import React, { useState } from 'react';
import { Input, Typography, Select, Card, List, Tag, Alert, Empty, Spin } from 'antd';
import { SearchOutlined, FileTextOutlined } from '@ant-design/icons';
import { getProjects, search } from '../services/api';
import RecordLink from '../components/RecordLink';

const { Title, Text } = Typography;

interface Project {
  id: string;
  code: string;
  name: string;
}

interface SearchResult {
  id: string;
  object_id: string;
  title: string;
  status: string;
  severity: string;
  created_at: string;
}

const typeColor: { [key: string]: string } = {
  NCR: 'red',
  MIR: 'gold',
  WIR: 'lime',
  RFI: 'geekblue',
  DPR: 'blue',
  MOM_ACTION: 'purple',
  MOM_RECORD: 'cyan',
  SITE_INSTRUCTION: 'orange',
  DELAY_ISSUE: 'volcano',
  SAFETY_VIOLATION: 'magenta',
};

const SearchPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ [key: string]: SearchResult[] }>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  React.useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const handleSearch = async () => {
    if (!query || query.length < 2) {
      setError('Please enter at least 2 characters to search.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await search(selectedProject, query);
      setResults(response.data.results);
      setTotal(response.data.total);
      setSearched(true);
    } catch (err) {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ margin: 0, color: '#1B2A4A', marginBottom: 24 }}>
        Search
      </Title>

      <Card style={{ borderRadius: 12, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={setSelectedProject}
            style={{ width: 250 }}
            options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Input
            placeholder="Search NCRs, DPRs, MOMs, Site Instructions..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            prefix={<SearchOutlined style={{ color: '#2E75B6' }} />}
            style={{ flex: 1 }}
            size="large"
          />
          <button
            onClick={handleSearch}
            style={{
              background: '#1B2A4A',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '0 24px',
              fontSize: 16,
              cursor: 'pointer',
            }}
          >
            Search
          </button>
        </div>
      </Card>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      )}

      {searched && !loading && total === 0 && (
        <Empty description={`No results found for "${query}"`} />
      )}

      {searched && !loading && total > 0 && (
        <>
          <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
            Found {total} result{total !== 1 ? 's' : ''} for "{query}"
          </Text>

          {Object.entries(results).map(([type, items]) => (
            <Card
              key={type}
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag color={typeColor[type] || 'default'}>{type.replace('_', ' ')}</Tag>
                  <Text type="secondary">{items.length} result{items.length !== 1 ? 's' : ''}</Text>
                </div>
              }
              style={{ borderRadius: 12, marginBottom: 16 }}
            >
              <List
                dataSource={items}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<FileTextOutlined style={{ fontSize: 20, color: '#2E75B6' }} />}
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <RecordLink recordId={item.object_id} recordType={type} projectId={selectedProject} />
                          {item.severity && (
                            <Tag color={item.severity === 'CRITICAL' ? 'red' : item.severity === 'MAJOR' ? 'orange' : 'green'}>
                              {item.severity}
                            </Tag>
                          )}
                          <Tag color="blue">{item.status?.replace('_', ' ')}</Tag>
                        </div>
                      }
                      description={
                        <div>
                          <Text>{item.title}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {new Date(item.created_at).toLocaleDateString('en-IN')}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          ))}
        </>
      )}
    </div>
  );
};

export default SearchPage;